import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import CabinScarcity from '../components/CabinScarcity';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { FrenchDatePicker } from '../components/ui/french-date-picker';
import { Ship, LogOut, Calendar, Users, Clock, ChevronRight, ChevronUp, ChevronDown, Check, X, Loader2, MapPin, Phone, Mail, FileText, ExternalLink, Image, FileDown, SendHorizonal, Plus, Trash2, Eye, Edit, Copy, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api','') || '';

const FLEET_PHOTOS = [
  '/images/hero_lagoon38_bow.jpg',
  '/images/hero_lagoon43_paddle.jpg',
  '/images/hero_lagoon43_cockpit.jpg',
  '/images/hero_lagoon38_aerial.jpg',
  '/images/hero_lagoon43_salon.jpg',
  '/images/hero_lagoon38_sailing.jpg',
  '/images/hero_lagoon43_cabine.jpg',
  '/images/hero_lagoon43_aerial.jpg',
];

const BOARDING_CARDS = {
  'tour-de-corse-croisiere-catamaran': 'https://static.wixstatic.com/media/ce6ce7_170fb96af2764aecb7eb7c526a48eb27~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20le%20tour%20de%20Corse%20sognudimare.png',
  'croisiere-catamaran-corse-du-sud': 'https://static.wixstatic.com/media/ce6ce7_2c02fe160efb49b6930f0c695a53e34f~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20corse%20du%20sud%20sognudimare.png',
  'croisiere-catamaran-ouest-corse': 'https://static.wixstatic.com/media/ce6ce7_bdc5406402ea4be3b94eeeb747d2da1a~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20ouest%20corse%20sognudimare.png',
  'croisiere-catamaran-sardaigne-corse': 'https://static.wixstatic.com/media/ce6ce7_68a8fb4c934c44cb909dfc0075f36d83~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20sardaigne%20et%20la%20corse%20du%20sud%20sognudimare.png',
  'croisiere-catamaran-grece-authentique': 'https://static.wixstatic.com/media/ce6ce7_95912f428d264151b75bce60481c048d~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20grèce%20authentique%20sognudimare.png',
  'croisiere-catamaran-grenadines-caraibes': 'https://static.wixstatic.com/media/ce6ce7_748110d102c448599459ce93b281fdb6~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20les%20iles%20grenadines%20sognudimare.png',
};

// Format date to French DD/MM/YYYY
function formatDateFr(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
}

// --- Agency Auth Context (local) ---
const AgencyContext = createContext(null);
function useAgency() { return useContext(AgencyContext); }

function AgencyProvider({ children }) {
  const [agency, setAgency] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('agency_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/agency/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setAgency(r.data))
        .catch(() => { localStorage.removeItem('agency_token'); setToken(null); })
        .finally(() => setLoading(false));
    } else { setLoading(false); }
  }, [token]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/agency/login`, { email, password });
    localStorage.setItem('agency_token', r.data.token);
    setToken(r.data.token);
    setAgency(r.data.agency);
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem('agency_token');
    setToken(null);
    setAgency(null);
  };

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <AgencyContext.Provider value={{ agency, token, loading, login, logout, authHeaders }}>
      {children}
    </AgencyContext.Provider>
  );
}

// --- Login Form ---
function AgencyLoginForm() {
  const { login } = useAgency();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success('Bienvenue !');
    } catch {
      toast.error('Identifiants incorrects');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_600,h_300,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/croisieres%20catamaran%20corse%20sognudimare.png"
            alt="SOGNUDIMARE" className="h-16 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold">Portail Agence Partenaire</h1>
          <p className="text-white/50 text-sm mt-1">Réservez pour vos clients en toute autonomie</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-xl space-y-4" data-testid="agency-login-form">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="agency-email-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Mot de passe</label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="agency-password-input" />
          </div>
          <Button type="submit" className="w-full bg-[#C4724F] hover:bg-[#a8603f] h-11" disabled={submitting} data-testid="agency-login-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Se connecter'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// --- Dashboard ---
function AgencyDashboardContent() {
  const { agency, authHeaders, logout } = useAgency();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cruises, setCruises] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('dashboard');
  const [showBookingForm, setShowBookingForm] = useState(null);
  const [showSunreef, setShowSunreef] = useState(false); // legacy name, now shows fleet gallery
  const [showQuoteForm, setShowQuoteForm] = useState(null);
  const [devisList, setDevisList] = useState([]);
  const [showDevisBuilder, setShowDevisBuilder] = useState(null);
  const [availRequests, setAvailRequests] = useState([]);
  const [contract, setContract] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, c, b, d, r, po] = await Promise.all([
        axios.get(`${API}/agency/dashboard`, { headers: authHeaders }),
        axios.get(`${API}/agency/cruises`, { headers: authHeaders }),
        axios.get(`${API}/agency/bookings`, { headers: authHeaders }),
        axios.get(`${API}/agency/devis`, { headers: authHeaders }),
        axios.get(`${API}/agency/availability-requests`, { headers: authHeaders }),
        axios.get(`${API}/agency/purchase-orders`, { headers: authHeaders }),
      ]);
      setStats(s.data);
      setCruises(c.data);
      setBookings(b.data);
      setDevisList(d.data);
      setAvailRequests(r.data);
      setPurchaseOrders(po.data);
      // Load contract separately (may not exist)
      try {
        const ct = await axios.get(`${API}/agency/partnership-contract`, { headers: authHeaders });
        setContract(ct.data);
      } catch { setContract(null); }
    } catch { toast.error('Erreur de chargement'); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = () => { logout(); navigate('/agence'); };

  if (!stats) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C4724F]" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <img src="https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_600,h_300,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/croisieres%20catamaran%20corse%20sognudimare.png"
              alt="SOGNUDIMARE" className="h-6 sm:h-8 shrink-0" />
            <div className="h-5 w-px bg-white/20 hidden sm:block" />
            {agency.logo_url && <img src={agency.logo_url} alt={agency.name} className="h-6 sm:h-8 object-contain shrink-0 hidden sm:block" />}
            <span className="font-semibold text-xs sm:text-sm truncate">{agency.name}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-white/50 text-sm hidden md:block">{agency.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/10 px-2 sm:px-3" data-testid="agency-logout-btn">
              <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
        {/* Tabs — scrollable on mobile */}
        <div className="max-w-7xl mx-auto px-4 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1 min-w-max">
            {[
              { id: 'dashboard', label: 'Tableau de bord', icon: Ship },
              { id: 'cruises', label: 'Croisières', icon: Calendar },
              { id: 'requests', label: 'Demandes', icon: Clock },
              { id: 'devis', label: 'Devis', icon: FileDown },
              { id: 'orders', label: 'Commandes', icon: FileText },
              { id: 'bookings', label: 'Réservations', icon: FileText },
              ...(contract ? [{ id: 'contract', label: 'Contrat', icon: FileText }] : []),
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.id ? 'border-[#C4724F] text-white' : 'border-transparent text-white/50 hover:text-white/70'
                }`} data-testid={`agency-tab-${t.id}`}>
                <t.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Tab — Step-by-step workflow guide */}
        {tab === 'dashboard' && (
          <AgencyDashboardTab
            stats={stats}
            bookings={bookings}
            contract={contract}
            availRequests={availRequests}
            devisList={devisList}
            purchaseOrders={purchaseOrders}
            onNavigate={setTab}
          />
        )}

        {/* Cruises Tab */}
        {tab === 'cruises' && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">Croisières avec places disponibles</h2>
              {agency && (
                (() => {
                  const isMarkup = (agency.commission_type || 'deduct') === 'markup';
                  const rate = agency.commission_rate || 0;
                  return (
                    <div className={`rounded-xl border-2 px-3 py-2 flex items-center gap-2.5 ${
                      isMarkup
                        ? 'bg-[#C4724F]/5 border-[#C4724F]/30'
                        : 'bg-[#2A9D8F]/5 border-[#2A9D8F]/30'
                    }`} data-testid="commission-mode-banner">
                      <span className="text-lg leading-none">{isMarkup ? '📈' : '📉'}</span>
                      <div className="text-xs leading-tight">
                        <div className={`font-bold ${isMarkup ? 'text-[#C4724F]' : 'text-[#2A9D8F]'}`}>
                          {isMarkup ? 'Commission AJOUTÉE' : 'Commission DÉDUITE'} · {rate}%
                        </div>
                        <div className="text-slate-600">
                          {isMarkup
                            ? 'Vous ajoutez votre marge au tarif catalogue'
                            : 'Vous vendez au tarif catalogue, commission reversée par SOGNUDIMARE'}
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
            {cruises.filter(c => c.total_available_dates > 0).length === 0 ? (
              <p className="text-gray-400 text-center py-12">Aucune croisière disponible actuellement</p>
            ) : (
              cruises.filter(c => c.total_available_dates > 0).map(cruise => {
                const urgentCount = (cruise.available_dates || []).filter(d => d.available_cabins > 0 && d.available_cabins <= 2).length;
                return (
                <div key={cruise.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`agency-cruise-${cruise.id}`}>
                  <div className="p-5 border-b bg-gradient-to-r from-[#0A2540] to-[#0A2540]/90">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-bold text-lg">{cruise.name}</h3>
                        <p className="text-white/50 text-sm">{cruise.duration} &middot; {cruise.total_available_dates} date(s) disponible(s)</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <span className="text-xs bg-[#2A9D8F]/20 text-[#2A9D8F] px-2 py-0.5 rounded font-medium">LAGOON 46 / 43 / 38</span>
                          <button onClick={() => setShowSunreef(true)}
                            className="text-xs text-white/50 hover:text-white flex items-center gap-1 transition-colors" data-testid={`view-boat-${cruise.id}`}>
                            <Image className="h-3 w-3" /> Voir le bateau
                          </button>
                          <a href={`/${cruise.slug}`} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-[#C4724F] hover:text-[#e88a63] flex items-center gap-1 transition-colors" data-testid={`view-program-${cruise.id}`}>
                            <ExternalLink className="h-3 w-3" /> Programme
                          </a>
                          {urgentCount > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-md"
                              data-testid={`agency-urgent-${cruise.id}`}
                            >
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-90" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                              </span>
                              {urgentCount === 1 ? '1 départ urgent' : `${urgentCount} départs urgents`}
                            </span>
                          )}
                        </div>
                      </div>
                      {BOARDING_CARDS[cruise.slug] ? (
                        <img src={BOARDING_CARDS[cruise.slug]} alt={cruise.name}
                          className="h-16 w-auto rounded-lg shadow-md border border-white/10" />
                      ) : (
                        <Ship className="h-6 w-6 text-[#C4724F]" />
                      )}
                    </div>
                  </div>
                  <div className="divide-y">
                    {cruise.available_dates.map(date => {
                      const cabinsLeft = date.available_cabins;
                      const isFull = cabinsLeft <= 0;
                      const isCritical = cabinsLeft === 1;
                      const isHigh = cabinsLeft === 2;
                      const rowAccent = isCritical
                        ? 'border-l-4 border-red-500 bg-red-50/40'
                        : isHigh
                        ? 'border-l-4 border-orange-400 bg-orange-50/40'
                        : isFull
                        ? 'opacity-60'
                        : '';
                      // Pricing breakdown for the agency
                      const catalogPrice = date.cabin_price || 0;
                      const isMarkup = (agency?.commission_type || 'deduct') === 'markup';
                      const commissionRate = agency?.commission_rate || 0;
                      const agencyPrice = isMarkup
                        ? Math.round(catalogPrice * (1 + commissionRate / 100))
                        : catalogPrice;
                      const gainPerPax = isMarkup
                        ? agencyPrice - catalogPrice
                        : Math.round(catalogPrice * commissionRate / 100);
                      return (
                      <div key={date.id} className={`p-4 hover:bg-gray-50 transition-colors ${rowAccent}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <div className="bg-[#C4724F]/10 rounded-lg p-2 sm:p-2.5 shrink-0">
                              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-[#C4724F]" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm">{formatDateFr(date.start_date)} → {formatDateFr(date.end_date)}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <CabinScarcity available={cabinsLeft} variant="compact" />
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-2 ml-0 sm:ml-auto pl-0 sm:pl-0">
                            {/* Pricing block */}
                            <div className="text-right pr-1 sm:pr-2 border-r border-slate-200">
                              <div className="flex items-baseline gap-1 justify-end">
                                <span className="text-[9px] text-slate-400 uppercase tracking-wider whitespace-nowrap">Catalogue</span>
                                <span className={`text-xs ${isMarkup ? 'text-slate-400 line-through' : 'text-slate-700 font-semibold'} tabular-nums whitespace-nowrap`}>
                                  {catalogPrice}€
                                </span>
                              </div>
                              {isMarkup && (
                                <div className="flex items-baseline gap-1 justify-end">
                                  <span className="text-[9px] text-[#C4724F] uppercase tracking-wider whitespace-nowrap font-semibold">Tarif agence</span>
                                  <span className="text-sm font-bold text-[#0A2540] tabular-nums whitespace-nowrap">
                                    {agencyPrice}€
                                  </span>
                                </div>
                              )}
                              <div className="flex items-baseline gap-1 justify-end">
                                <span className="text-[9px] text-[#2A9D8F] uppercase tracking-wider whitespace-nowrap font-semibold">Votre gain</span>
                                <span className="text-sm font-bold text-[#2A9D8F] tabular-nums whitespace-nowrap">
                                  +{gainPerPax}€<span className="text-[9px] font-normal">/pers</span>
                                </span>
                              </div>
                            </div>
                            <Button size="sm" variant="outline" className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 h-8 text-xs"
                              onClick={() => { setShowDevisBuilder({ cruise, date }); setTab('devis'); }}
                              data-testid={`agency-quote-${date.id}`}
                              disabled={isFull}>
                              <FileDown className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Devis</span>
                            </Button>
                            <Button size="sm" className="bg-[#C4724F] hover:bg-[#a8603f] h-8 text-xs"
                              onClick={() => setShowBookingForm({ cruise, date })}
                              data-testid={`agency-book-${date.id}`}
                              disabled={isFull}>
                              <span className="hidden sm:inline">Demander au capitaine</span>
                              <span className="sm:hidden">Demander</span>
                              <ChevronRight className="h-3.5 w-3.5 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
                );
              })
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {/* Requests Tab */}
        {tab === 'requests' && (
          <AvailabilityRequestsTab requests={availRequests} onRefresh={load}
            onCreateDevis={(req) => {
              setShowDevisBuilder({
                cruise: { id: req.cruise_id, name: req.cruise_name, duration: req.duration || '', departure_port: req.departure_port || 'Ajaccio' },
                date: { id: req.date_id, start_date: req.start_date, end_date: req.end_date, cabin_price: req.price_per_person_base || req.cabin_price || 0, available_cabins: req.cabins_needed || 1 },
                prefill: { client_name: req.client_name || '', passenger_count: req.passenger_count || 2, is_solo: req.is_solo || false }
              });
              setTab('devis');
            }} />
        )}

        {/* Devis Tab */}
        {tab === 'devis' && (
          <AgencyDevisTab devisList={devisList} cruises={cruises} authHeaders={authHeaders} onRefresh={load}
            showDevisBuilder={showDevisBuilder} setShowDevisBuilder={setShowDevisBuilder} />
        )}

        {/* Purchase Orders / Invoices Tab */}
        {tab === 'orders' && (
          <PurchaseOrdersTab orders={purchaseOrders} requests={availRequests} cruises={cruises} authHeaders={authHeaders} onRefresh={load} />
        )}

        {tab === 'bookings' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Mes réservations</h2>
            {bookings.length === 0 ? (
              <p className="text-gray-400 text-center py-12">Aucune réservation</p>
            ) : (
              <div className="bg-white rounded-xl border divide-y">
                {bookings.map(b => <BookingRow key={b.id} booking={b} detailed />)}
              </div>
            )}
          </div>
        )}

        {/* Contract tab */}
        {tab === 'contract' && contract && (
          <AgencyContractTab contract={contract} />
        )}
      </div>

      {/* Availability Request Modal */}
      {showBookingForm && (
        <AvailabilityRequestModal
          cruise={showBookingForm.cruise}
          date={showBookingForm.date}
          authHeaders={authHeaders}
          onClose={() => setShowBookingForm(null)}
          onSuccess={() => { setShowBookingForm(null); load(); setTab('requests'); }}
        />
      )}

      {/* Fleet Gallery Modal */}
      {showSunreef && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowSunreef(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="fleet-gallery">
            <div className="bg-[#0A2540] text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="font-bold text-lg">FLOTTE LAGOON 46 / 43 / 38</h2>
                <p className="text-white/50 text-sm">Catamarans récents &mdash; 4 cabines doubles &mdash; 12 à 14 mètres</p>
              </div>
              <button onClick={() => setShowSunreef(false)} className="text-white/50 hover:text-white text-xl">&times;</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4">
              {FLEET_PHOTOS.map((src, i) => (
                <img key={i} src={src} alt={`Flotte Lagoon - Photo ${i+1}`}
                  className="w-full h-48 object-cover rounded-lg hover:opacity-90 transition-opacity cursor-pointer" />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Step-by-step Dashboard ──
const WORKFLOW_STEPS_FULL = [
  {
    num: 1,
    key: 'contract',
    title: 'Signer le contrat de partenariat',
    description: 'Avant toute chose, vous devez consulter et signer le contrat de partenariat commercial entre votre agence et SOGNUDIMARE. Ce contrat définit les conditions de collaboration : tarifs, commissions, conditions de paiement et responsabilités de chaque partie.',
    action: 'Aller à l\'onglet Contrat',
    actionTab: 'contract',
    howTo: 'Cliquez sur l\'onglet "Contrat" → Consultez les 11 articles → Cliquez "Consulter et signer" → Renseignez votre nom → Signez → Confirmez.',
  },
  {
    num: 2,
    key: 'browse',
    title: 'Parcourir les croisières disponibles',
    description: 'Consultez le catalogue des croisières avec les dates, tarifs et places disponibles. Chaque croisière affiche le nombre de cabines restantes, le prix par personne et le programme détaillé.',
    action: 'Voir les croisières',
    actionTab: 'cruises',
    howTo: 'Onglet "Croisières disponibles" → Parcourez les itinéraires → Vérifiez les dates et places → Cliquez "Programme" pour voir le détail.',
  },
  {
    num: 3,
    key: 'request',
    title: 'Demander la disponibilité au capitaine',
    description: 'Quand votre client est intéressé par une croisière, envoyez une demande de disponibilité au capitaine. Indiquez le nombre de passagers, le nom du client et un message si nécessaire. Le capitaine répondra sous 48h.',
    action: 'Faire une demande',
    actionTab: 'cruises',
    howTo: 'Sur une date → Cliquez "Demander au capitaine" → Renseignez le nombre de passagers et le nom du client → Envoyez la demande.',
  },
  {
    num: 4,
    key: 'confirm',
    title: 'Confirmation et blocage des places (3 jours)',
    description: 'Si le capitaine confirme, les places sont automatiquement bloquées pendant 3 jours. Un compteur de temps s\'affiche. C\'est durant ce délai que vous devez préparer et envoyer le devis à votre client. Si le délai expire, les places sont libérées automatiquement.',
    action: 'Voir mes demandes',
    actionTab: 'requests',
    howTo: 'Onglet "Demandes" → Suivez le statut de vos demandes → Quand le statut passe à "Confirmé", les places sont bloquées. Attention : vous avez 3 jours maximum.',
  },
  {
    num: 5,
    key: 'devis',
    title: 'Créer le devis pour votre client',
    description: 'Une fois la disponibilité confirmée, préparez le devis pour votre client. Le devis est pré-rempli avec le prix de base Sognudimare. Ajoutez votre marge commerciale (%), renseignez les informations du client, puis validez. Le PDF est généré automatiquement avec vos couleurs et vos CGV.',
    action: 'Créer un devis',
    actionTab: 'requests',
    howTo: 'Dans "Demandes" → Sur la demande confirmée, cliquez "Créer le devis client" → Ajustez la marge avec le curseur → Complétez les infos client → Sauvegardez.',
  },
  {
    num: 6,
    key: 'send',
    title: 'Envoyer le devis au client pour signature',
    description: 'Envoyez le devis à votre client par email. Il recevra un lien vers une page sécurisée où il pourra consulter le devis complet avec les CGV, puis le signer électroniquement. Vous serez notifié dès que le client a signé.',
    action: 'Mes devis',
    actionTab: 'devis',
    howTo: 'Onglet "Mes devis" → Cliquez "Envoyer" sur le devis → Le client reçoit un email avec un lien → Il consulte et signe en ligne.',
  },
  {
    num: 7,
    key: 'purchase_order',
    title: 'Bon de commande',
    description: 'Une fois le devis signé par le client, générez le bon de commande officiel. Ce document commercial fait foi entre Sognudimare (vendeur) et le client final, avec votre agence en tant que mandataire. Il se transformera automatiquement en facture acquittée dès réception du paiement.',
    action: 'Mes commandes',
    actionTab: 'orders',
    howTo: 'Onglet "Commandes" → Sur une demande confirmée avec devis signé, cliquez "Créer le bon de commande" → Complétez les infos client → Le PDF est généré automatiquement avec le n° BC-XXXX.',
  },
  {
    num: 8,
    key: 'payment',
    title: 'Payer Sognudimare (échéancier)',
    description: 'Après signature du client, réglez Sognudimare selon l\'échéancier affiché dans votre demande. L\'échéancier dépend de la date de départ : plus de 90 jours (35%+35%+30%), entre 60-90 jours (50%+50%), moins de 60 jours (100%). Le paiement intégral doit être effectué 30 jours avant le départ.',
    action: 'Voir les échéances',
    actionTab: 'requests',
    howTo: 'Dans "Demandes" → L\'échéancier est affiché sur chaque demande confirmée → Effectuez les paiements par virement ou carte → Sognudimare confirme chaque échéance.',
  },
  {
    num: 9,
    key: 'booking',
    title: 'Réservation confirmée',
    description: 'Quand tous les paiements sont reçus, la réservation est automatiquement créée dans le système. Les cartes d\'embarquement seront envoyées par email 3 jours avant le départ. La croisière est définitivement réservée pour votre client.',
    action: 'Mes réservations',
    actionTab: 'bookings',
    howTo: 'Onglet "Mes réservations" → Retrouvez toutes vos réservations confirmées → Les cartes d\'embarquement seront envoyées automatiquement.',
  },
];

function AgencyDashboardTab({ stats, bookings, contract, availRequests, devisList, purchaseOrders, onNavigate }) {
  // Compute step statuses
  const contractSigned = contract?.status === 'active' || !!contract?.agency_signed_at;
  const contractPending = contract && !contractSigned;

  const hasRequests = availRequests?.length > 0;
  const hasApprovedRequest = availRequests?.some(r => r.status === 'approved');
  const hasDevis = devisList?.length > 0;
  const hasSentDevis = devisList?.some(d => d.status === 'sent' || d.status === 'signed');
  const hasSignedDevis = devisList?.some(d => d.status === 'signed');
  const hasConfirmedRequest = availRequests?.some(r => r.status === 'confirmed');
  const hasBookings = bookings?.length > 0;
  const hasPurchaseOrders = purchaseOrders?.length > 0;

  const stepStatus = (key) => {
    switch(key) {
      case 'contract': return contractSigned ? 'done' : contractPending ? 'active' : 'todo';
      case 'browse': return hasRequests ? 'done' : 'active';
      case 'request': return hasRequests ? 'done' : 'active';
      case 'confirm': return hasApprovedRequest || hasConfirmedRequest ? 'done' : hasRequests ? 'active' : 'todo';
      case 'devis': return hasDevis ? 'done' : hasApprovedRequest ? 'active' : 'todo';
      case 'send': return hasSentDevis ? 'done' : hasDevis ? 'active' : 'todo';
      case 'purchase_order': return hasPurchaseOrders ? 'done' : hasSignedDevis ? 'active' : 'todo';
      case 'payment': return hasConfirmedRequest ? 'done' : hasPurchaseOrders ? 'active' : 'todo';
      case 'booking': return hasBookings ? 'done' : hasConfirmedRequest ? 'active' : 'todo';
      default: return 'todo';
    }
  };

  const completedSteps = WORKFLOW_STEPS_FULL.filter(s => stepStatus(s.key) === 'done').length;
  const progressPercent = Math.round((completedSteps / WORKFLOW_STEPS_FULL.length) * 100);

  return (
    <div className="space-y-6" data-testid="agency-dashboard-tab">
      {/* Monthly performance card (compact) */}
      {stats.month && (() => {
        const isMarkup = (stats.commission_type || 'deduct') === 'markup';
        const gradient = isMarkup
          ? 'from-[#C4724F] via-[#A0552F] to-[#0A2540]'
          : 'from-[#2A9D8F] via-[#0F766E] to-[#0A2540]';
        const MONTH_LABELS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
        const MONTH_SHORT_FR = ['Janv','Févr','Mars','Avr','Mai','Juin','Juil','Août','Sept','Oct','Nov','Déc'];
        const now = new Date();
        const currentLabel = `${MONTH_LABELS_FR[now.getMonth()]} ${now.getFullYear()}`;
        const byMonth = stats.by_month || [];
        const maxGain = Math.max(1, ...byMonth.map(m => m.gain_total || 0));
        return (
          <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} text-white p-5 shadow-lg`} data-testid="agency-monthly-card">
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-white/70 font-semibold">Vos performances · <span className="capitalize">{currentLabel}</span></div>
                  <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                    <span className="font-display text-3xl sm:text-4xl font-bold leading-none tabular-nums">
                      {Math.round(stats.month.gain_total).toLocaleString('fr-FR')}€
                    </span>
                    <span className="text-xs text-white/75">de gain ce mois</span>
                  </div>
                  <div className="text-[11px] text-white/65 mt-0.5">
                    {stats.month.bookings} résa · {stats.month.cabins} cab. · {stats.month.passengers} pax
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full backdrop-blur-md bg-white/15 border border-white/20 shrink-0">
                  <span className="text-xs leading-none">{isMarkup ? '📈' : '📉'}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">{isMarkup ? 'Ajoutée' : 'Déduite'} {stats.commission_rate}%</span>
                </div>
              </div>

              {/* Inline sub stats */}
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/15 text-center">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-white/55">Catalogue mois</div>
                  <div className="font-display text-sm font-bold tabular-nums">{Math.round(stats.month.catalog_total).toLocaleString('fr-FR')}€</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-white/55">Gain {now.getFullYear()}</div>
                  <div className="font-display text-sm font-bold tabular-nums">{Math.round(stats.year.gain_total).toLocaleString('fr-FR')}€</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-white/55">Gain total</div>
                  <div className="font-display text-sm font-bold tabular-nums">{Math.round(stats.all_time.gain_total).toLocaleString('fr-FR')}€</div>
                </div>
              </div>

              {/* Month sparkline (12 months) — discreet */}
              {byMonth.length > 0 && (
                <details className="mt-3 group" data-testid="agency-monthly-series">
                  <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-white/65 hover:text-white transition-colors flex items-center gap-1 list-none">
                    <span className="group-open:rotate-90 transition-transform inline-block">▸</span>
                    Évolution sur 12 mois
                  </summary>
                  <div className="mt-2 flex items-end justify-between gap-1 h-14">
                    {byMonth.map(m => {
                      const isCurrent = m.iso === stats.current_month_iso;
                      const pct = (m.gain_total / maxGain) * 100;
                      return (
                        <div key={m.iso} className="flex-1 flex flex-col items-center group/bar min-w-0" title={`${MONTH_LABELS_FR[m.month - 1]} ${m.year} : ${Math.round(m.gain_total).toLocaleString('fr-FR')}€`}>
                          <div className={`w-full rounded-t-sm transition-all ${isCurrent ? 'bg-white' : 'bg-white/35 group-hover/bar:bg-white/60'}`} style={{ height: `${Math.max(3, pct)}%` }} />
                          <div className={`mt-1 text-[8px] tabular-nums ${isCurrent ? 'text-white font-bold' : 'text-white/55'}`}>
                            {MONTH_SHORT_FR[m.month - 1]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          </div>
        );
      })()}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total réservations', value: stats.total_bookings, color: 'bg-blue-50 text-blue-700' },
          { label: 'En attente', value: stats.pending, color: 'bg-amber-50 text-amber-700' },
          { label: 'Approuvées', value: stats.approved, color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Commission totale', value: `${stats.total_commission.toFixed(0)}€`, color: 'bg-purple-50 text-purple-700' },
        ].map((s, i) => (
          <div key={i} className={`rounded-xl p-4 ${s.color}`} data-testid={`agency-stat-${i}`}>
            <p className="text-xs font-medium opacity-70">{s.label}</p>
            <p className="text-2xl font-bold mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl border p-5" data-testid="workflow-progress">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#0A2540]">Progression du workflow</h3>
          <span className="text-sm font-semibold text-[#C4724F]">{completedSteps}/{WORKFLOW_STEPS_FULL.length} étapes</span>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#0A2540] to-[#C4724F] rounded-full transition-all duration-700"
            style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      {/* Step-by-step workflow guide — ALWAYS VISIBLE */}
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="workflow-guide-full">
        <div className="bg-[#0A2540] px-6 py-4">
          <h3 className="text-white font-bold">Guide complet — Comment réserver pour votre client</h3>
          <p className="text-white/50 text-sm mt-0.5">Suivez ces 8 étapes dans l'ordre pour finaliser une réservation</p>
        </div>

        <div className="divide-y">
          {WORKFLOW_STEPS_FULL.map((step) => {
            const status = stepStatus(step.key);
            const isDone = status === 'done';
            const isActive = status === 'active';

            return (
              <div key={step.num}
                className={`p-5 transition-colors ${isDone ? 'bg-emerald-50/30' : isActive ? 'bg-amber-50/30' : 'bg-white'}`}
                data-testid={`workflow-step-${step.num}`}
              >
                <div className="flex gap-4">
                  {/* Step number / status */}
                  <div className="flex-shrink-0 pt-0.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                      isDone ? 'bg-emerald-500 text-white' :
                      isActive ? 'bg-[#C4724F] text-white' :
                      'bg-gray-200 text-gray-500'
                    }`}>
                      {isDone ? <Check className="h-5 w-5" /> : step.num}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                      <div className="min-w-0">
                        <h4 className={`font-bold text-sm ${isDone ? 'text-emerald-700' : isActive ? 'text-[#0A2540]' : 'text-gray-400'}`}>
                          Étape {step.num} — {step.title}
                        </h4>
                        <p className={`text-sm mt-1 leading-relaxed ${isDone ? 'text-emerald-600/80' : isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                          {step.description}
                        </p>

                        {/* How-to box */}
                        <div className={`mt-3 rounded-lg p-3 border text-xs leading-relaxed ${
                          isDone ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                          isActive ? 'bg-blue-50 border-blue-200 text-blue-800' :
                          'bg-gray-50 border-gray-200 text-gray-500'
                        }`}>
                          <span className="font-semibold">Comment faire :</span> {step.howTo}
                        </div>
                      </div>

                      {/* Action button */}
                      {(isActive || isDone) && (
                        <button
                          onClick={() => onNavigate(step.actionTab)}
                          className={`self-start flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap mt-2 sm:mt-0 ${
                            isActive ? 'bg-[#C4724F] text-white hover:bg-[#a8603f]' :
                            'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid={`workflow-action-${step.num}`}
                        >
                          {step.action}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent bookings */}
      {bookings.length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Dernières réservations</h3>
          <div className="space-y-3">
            {bookings.slice(0, 5).map(b => (
              <BookingRow key={b.id} booking={b} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// --- Booking Row ---
function BookingRow({ booking: b, detailed }) {
  const statusConfig = {
    pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-800' },
    approved: { label: 'Approuvée', cls: 'bg-emerald-100 text-emerald-800' },
    rejected: { label: 'Refusée', cls: 'bg-red-100 text-red-800' },
  };
  const s = statusConfig[b.status] || statusConfig.pending;

  return (
    <div className={`${detailed ? 'p-5' : 'p-3'} flex items-center justify-between`} data-testid={`agency-booking-${b.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${b.status === 'approved' ? 'bg-emerald-500' : b.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'}`} />
        <div>
          <p className="font-medium text-gray-900 text-sm">{b.customer_name}</p>
          <p className="text-xs text-gray-500">{b.cruise_name} &middot; {formatDateFr(b.start_date)} → {formatDateFr(b.end_date)}</p>
          {detailed && b.admin_notes && <p className="text-xs text-gray-400 mt-1 italic">Note: {b.admin_notes}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-gray-700">{b.total_amount}€</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>
      </div>
    </div>
  );
}

// --- Availability Request Modal ---
function AvailabilityRequestModal({ cruise, date, authHeaders, onClose, onSuccess }) {
  const [form, setForm] = useState({
    client_name: '', client_info: '', passenger_count: 2, is_solo: false, message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const maxSpots = date.available_cabins * 2;
  const pricePerPerson = date.cabin_price;
  const isWeek2 = cruise.duration?.includes('14');
  const caissePP = isWeek2 ? 480 : 240;
  const cabinsNeeded = form.is_solo ? form.passenger_count : Math.ceil(form.passenger_count / 2);
  const baseCost = form.is_solo ? Math.round(pricePerPerson * 2 * 0.9 * form.passenger_count) : form.passenger_count * pricePerPerson;
  const totalSognu = baseCost + form.passenger_count * caissePP;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/agency/availability-request`, {
        cruise_id: cruise.id, date_id: date.id,
        passenger_count: form.passenger_count, is_solo: form.is_solo,
        client_name: form.client_name, client_info: form.client_info, message: form.message,
      }, { headers: authHeaders });
      toast.success('Demande envoyée au capitaine !');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="availability-request-modal">
        <div className="bg-[#0A2540] text-white p-6 rounded-t-2xl">
          <h2 className="font-bold text-lg">Demander la disponibilité</h2>
          <p className="text-white/60 text-sm">{cruise.name}</p>
          <p className="text-white/40 text-xs">{formatDateFr(date.start_date)} → {formatDateFr(date.end_date)}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs bg-white/10 px-2 py-1 rounded">{date.available_cabins} cabine(s) dispo</span>
            <span className="text-xs bg-[#C4724F]/80 px-2 py-1 rounded">{pricePerPerson}€/pers</span>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Passagers</label>
            <select className="w-full border rounded-md h-10 px-3 text-sm" value={form.passenger_count}
              onChange={e => setForm(f => ({...f, passenger_count: parseInt(e.target.value), is_solo: parseInt(e.target.value) === 1 ? f.is_solo : false}))} data-testid="req-pax">
              {Array.from({ length: maxSpots }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n} passager{n > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
          {form.passenger_count === 1 && (
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border cursor-pointer">
              <input type="checkbox" checked={form.is_solo} onChange={e => setForm(f => ({...f, is_solo: e.target.checked}))}
                className="rounded border-gray-300 text-[#C4724F] focus:ring-[#C4724F]" />
              <div><span className="text-sm font-medium text-gray-700">Cabine solo</span></div>
            </label>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Nom du client (optionnel)</label>
            <Input value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))}
              placeholder="Jean Dupont" data-testid="req-client-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Message pour le capitaine</label>
            <Textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))}
              placeholder="Informations complémentaires..." rows={3} data-testid="req-message" />
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase">Récapitulatif prix Sognudimare</p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{form.passenger_count} pax × {pricePerPerson}€{form.is_solo ? ' (solo -10%)' : ''}</span>
              <span className="font-medium">{baseCost}€</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Caisse de bord ({caissePP}€/pers)</span>
              <span className="font-medium">{form.passenger_count * caissePP}€</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-bold">
              <span>Total Sognudimare</span>
              <span className="text-[#C4724F]">{totalSognu}€</span>
            </div>
            <p className="text-xs text-gray-400">Ce montant sera à régler à Sognudimare. Votre marge sera ajoutée sur le devis client.</p>
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="flex-1 bg-[#C4724F] hover:bg-[#a8603f]" disabled={submitting} data-testid="req-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Envoyer au capitaine'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Availability Requests Tab ---
const REQ_STATUS = {
  pending: { label: 'En attente du capitaine', cls: 'bg-amber-100 text-amber-700', icon: '⏳', step: 1 },
  approved: { label: 'Disponibilité confirmée', cls: 'bg-emerald-100 text-emerald-700', icon: '✓', step: 2 },
  rejected: { label: 'Non disponible', cls: 'bg-red-100 text-red-700', icon: '✗', step: 0 },
  expired: { label: 'Délai expiré', cls: 'bg-gray-100 text-gray-500', icon: '⊘', step: 0 },
  confirmed: { label: 'Réservation confirmée', cls: 'bg-blue-100 text-blue-700', icon: '✓✓', step: 4 },
};

const WORKFLOW_STEPS = [
  { num: 1, title: 'Demande', desc: 'Vous demandez la disponibilité au capitaine' },
  { num: 2, title: 'Confirmation', desc: 'Le capitaine confirme et bloque les places (3 jours)' },
  { num: 3, title: 'Devis client', desc: 'Vous préparez et envoyez le devis à votre client' },
  { num: 4, title: 'Paiement', desc: 'Vous réglez Sognudimare, la réservation est validée' },
];

function AvailabilityRequestsTab({ requests, onRefresh, onCreateDevis }) {
  const [showGuide, setShowGuide] = useState(requests.length === 0);
  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('agency_token')}` };

  const deleteRequest = async (reqId) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API}/agency/availability-requests/${reqId}`, { headers: authHeaders });
      toast.success('Demande supprimée');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  useEffect(() => {
    const timer = setInterval(onRefresh, 30000);
    return () => clearInterval(timer);
  }, [onRefresh]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Mes demandes</h2>
        <Button variant="outline" size="sm" onClick={() => setShowGuide(!showGuide)} className="h-8 text-xs gap-1" data-testid="toggle-guide">
          {showGuide ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          Comment ça marche ?
        </Button>
      </div>

      {/* Workflow guide */}
      {showGuide && (
        <div className="bg-[#0A2540] rounded-2xl p-6 text-white" data-testid="workflow-guide">
          <h3 className="font-bold text-sm uppercase tracking-wider text-white/60 mb-4">Fonctionnement</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {WORKFLOW_STEPS.map((s, i) => (
              <div key={s.num} className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-full bg-[#C4724F] flex items-center justify-center text-xs font-bold">{s.num}</span>
                  {i < 3 && <div className="flex-1 h-px bg-white/20" />}
                </div>
                <p className="text-sm font-semibold text-white">{s.title}</p>
                <p className="text-xs text-white/50 mt-0.5">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-white/10 rounded-lg p-3 text-xs text-white/70">
            <p><strong className="text-white">Important :</strong> Les places sont bloquées <strong className="text-[#C4724F]">3 jours maximum</strong> après confirmation. 
            Passé ce délai, elles sont automatiquement libérées. Seul le capitaine peut prolonger. 
            Le paiement intégral doit être effectué <strong className="text-[#C4724F]">30 jours avant le départ</strong> (caisse de bord comprise).</p>
          </div>
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Clock className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">Aucune demande pour le moment</p>
          <p className="text-gray-300 text-sm mt-1">Allez dans "Croisières disponibles" et cliquez "Demander au capitaine"</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(req => {
            const st = REQ_STATUS[req.status] || REQ_STATUS.pending;
            const holdUntil = req.hold_until ? new Date(req.hold_until) : null;
            const now = new Date();
            const holdRemaining = holdUntil ? Math.max(0, Math.floor((holdUntil - now) / 3600000)) : 0;
            const currentStep = st.step;

            return (
              <div key={req.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`req-row-${req.id}`}>
                {/* Step progress bar */}
                {currentStep > 0 && (
                  <div className="flex h-1.5">
                    {[1,2,3,4].map(s => (
                      <div key={s} className={`flex-1 ${s <= currentStep ? 'bg-[#C4724F]' : 'bg-gray-100'}`} />
                    ))}
                  </div>
                )}

                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.icon} {st.label}</span>
                        <span className="text-sm font-bold text-[#0A2540]">{req.cruise_name}</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {formatDateFr(req.start_date)} → {formatDateFr(req.end_date)} — {req.passenger_count} pax{req.is_solo ? ' (Solo)' : ''} — {req.cabins_needed} cabine(s)
                      </p>
                      {req.client_name && <p className="text-xs text-gray-500 mt-0.5">Client : <strong>{req.client_name}</strong></p>}
                      {req.admin_response && (
                        <p className="text-xs text-gray-500 mt-1.5 italic bg-gray-50 px-3 py-2 rounded-lg border-l-2 border-[#C4724F]">
                          Capitaine : "{req.admin_response}"
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-[#C4724F]">{req.total_sognudimare}€</span>
                      <p className="text-xs text-gray-400">Prix Sognudimare</p>
                      <button onClick={() => deleteRequest(req.id)}
                        className="text-xs text-red-400 hover:text-red-600 mt-1 flex items-center gap-1 ml-auto"
                        data-testid={`delete-req-${req.id}`}>
                        <Trash2 className="h-3 w-3" /> Supprimer
                      </button>
                    </div>
                  </div>

                  {/* PENDING — waiting */}
                  {req.status === 'pending' && (
                    <div className="mt-3 bg-amber-50 rounded-lg p-3 border border-amber-200 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
                      <span className="text-sm text-amber-800">En attente de la réponse du capitaine...</span>
                    </div>
                  )}

                  {/* REJECTED */}
                  {req.status === 'rejected' && (
                    <div className="mt-3 bg-red-50 rounded-lg p-3 border border-red-200">
                      <p className="text-sm text-red-700">Le capitaine n'a pas pu confirmer cette disponibilité. N'hésitez pas à proposer d'autres dates.</p>
                    </div>
                  )}

                  {/* EXPIRED */}
                  {req.status === 'expired' && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3 border">
                      <p className="text-sm text-gray-500">Le délai de blocage a expiré. Les places ont été libérées. Vous pouvez refaire une demande.</p>
                    </div>
                  )}

                  {/* APPROVED — main action zone */}
                  {req.status === 'approved' && (
                    <div className="mt-4 space-y-3">
                      {/* Hold countdown */}
                      {holdUntil && (
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-800">Places bloquées</span>
                            </div>
                            <span className="text-sm font-bold text-amber-700">
                              {holdRemaining > 24 ? `${Math.floor(holdRemaining/24)}j ${holdRemaining%24}h` : `${holdRemaining}h`} restantes
                            </span>
                          </div>
                          <p className="text-xs text-amber-600 mt-1">
                            Jusqu'au {holdUntil.toLocaleDateString('fr-FR')} à {holdUntil.toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                            {req.hold_extended && ' (prolongé par le capitaine)'}
                          </p>
                        </div>
                      )}

                      {/* ACTION: Create Devis button */}
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200" data-testid={`req-action-devis-${req.id}`}>
                        <p className="text-sm text-emerald-800 mb-3">
                          <strong>Étape suivante :</strong> Préparez le devis pour votre client. Le devis sera pré-rempli avec les informations de cette croisière. Ajoutez votre marge et envoyez-le directement par email.
                        </p>
                        <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90 w-full sm:w-auto"
                          onClick={() => onCreateDevis(req)}
                          data-testid={`req-create-devis-${req.id}`}>
                          <FileDown className="h-4 w-4 mr-2" /> Créer le devis client
                        </Button>
                      </div>

                      {/* Payment schedule */}
                      {req.payment_schedule?.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3 border">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Échéancier de paiement à Sognudimare</p>
                          <div className="space-y-1.5">
                            {req.payment_schedule.map((p, i) => (
                              <div key={i} className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">{p.label}</span>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{p.amount}€</span>
                                  {p.status === 'paid' ? (
                                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Payé</span>
                                  ) : p.square_link ? (
                                    <a href={p.square_link} target="_blank" rel="noreferrer"
                                      className="text-xs bg-[#0A2540] text-white px-3 py-1 rounded-full hover:bg-[#0A2540]/90 flex items-center gap-1"
                                      data-testid={`pay-square-${i}`}>
                                      <CreditCard className="h-3 w-3" /> Payer par carte
                                    </a>
                                  ) : (
                                    <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Avant le {formatDateFr(p.due_date)}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-blue-700 mb-1">Option virement bancaire (0% frais)</p>
                            <div className="text-xs text-blue-900 font-mono space-y-0.5">
                              <p>IBAN : FR76 2573 3000 0100 0001 4479 108</p>
                              <p>BIC : PSSSFR22</p>
                              <p className="font-sans text-blue-600">GREEN-GOT — NICOLAS LLORENS EI</p>
                            </div>
                            <p className="text-xs text-blue-500 mt-1">Indiquez la référence : {req.id?.slice(0,8)} — {req.client_name}</p>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">Paiement intégral requis 30 jours avant le départ.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* CONFIRMED (fully paid) */}
                  {req.status === 'confirmed' && (
                    <div className="mt-3 bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Check className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">Réservation confirmée et payée</span>
                      </div>
                      <p className="text-xs text-blue-600">Paiement complet reçu par Sognudimare. La croisière est définitivement réservée.</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── AGENCY DEVIS TAB (full quote system) ──

const DEVIS_STATUS = {
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Envoyé', cls: 'bg-blue-100 text-blue-700' },
  viewed: { label: 'Consulté', cls: 'bg-indigo-100 text-indigo-700' },
  signed: { label: 'Signé', cls: 'bg-emerald-100 text-emerald-700' },
};

function AgencyDevisTab({ devisList, cruises, authHeaders, onRefresh, showDevisBuilder, setShowDevisBuilder }) {
  const [sending, setSending] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const handleSend = async (dv) => {
    if (!dv.client_email) { toast.error("Ajoutez d'abord l'email du client"); return; }
    setSending(dv.id);
    try {
      const r = await axios.post(`${API}/agency/devis/${dv.id}/send`, {}, { headers: authHeaders });
      toast.success(r.data.message);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
    finally { setSending(null); }
  };

  const handlePdf = async (dv) => {
    setDownloading(dv.id);
    try {
      const r = await axios.get(`${API}/agency/devis/${dv.id}/pdf`, { headers: authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Erreur PDF'); }
    finally { setDownloading(null); }
  };

  const handleDelete = async (dv) => {
    if (!window.confirm(`Supprimer le devis ${dv.devis_number} ?`)) return;
    setDeleting(dv.id);
    try {
      await axios.delete(`${API}/agency/devis/${dv.id}`, { headers: authHeaders });
      toast.success('Devis supprimé');
      onRefresh();
    } catch { toast.error('Erreur suppression'); }
    finally { setDeleting(null); }
  };

  if (showDevisBuilder) {
    return <DevisBuilder init={showDevisBuilder} cruises={cruises} authHeaders={authHeaders}
      onClose={() => setShowDevisBuilder(null)} onCreated={() => { setShowDevisBuilder(null); onRefresh(); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Mes devis</h2>
        <Button onClick={() => setShowDevisBuilder({})} className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="new-devis-btn">
          <Plus className="h-4 w-4 mr-1" /> Nouveau devis
        </Button>
      </div>

      {devisList.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileDown className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">Aucun devis pour le moment</p>
          <p className="text-gray-300 text-sm mt-1">Cliquez sur "Devis" dans les croisières disponibles pour en créer un</p>
        </div>
      ) : (
        <div className="space-y-3">
          {devisList.map(dv => {
            const st = DEVIS_STATUS[dv.status] || DEVIS_STATUS.draft;
            return (
              <div key={dv.id} className="bg-white rounded-xl border p-5 hover:shadow-sm transition-shadow" data-testid={`devis-row-${dv.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-[#0A2540]">{dv.devis_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="text-sm text-gray-700 font-medium truncate">{dv.cruise_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDateFr(dv.start_date)} → {formatDateFr(dv.end_date)}
                      {dv.client_name && <> &mdash; <span className="text-gray-500">{dv.client_name}</span></>}
                      {dv.client_email && <> ({dv.client_email})</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg font-bold text-[#C4724F] whitespace-nowrap">{dv.total}€</span>
                    <Button size="sm" variant="outline" onClick={() => handlePdf(dv)} disabled={downloading === dv.id}
                      className="h-8 text-xs" data-testid={`devis-pdf-${dv.id}`}>
                      {downloading === dv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Eye className="h-3.5 w-3.5 mr-1" /> PDF</>}
                    </Button>
                    {dv.status !== 'signed' && (
                      <Button size="sm" onClick={() => handleSend(dv)} disabled={sending === dv.id || !dv.client_email}
                        className="bg-[#a11a3c] hover:bg-[#a11a3c]/90 h-8 text-xs" data-testid={`devis-send-${dv.id}`}>
                        {sending === dv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><SendHorizonal className="h-3.5 w-3.5 mr-1" /> Envoyer</>}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(dv)} disabled={deleting === dv.id}
                      className="h-8 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`devis-delete-${dv.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {dv.margin_percent > 0 && (
                  <p className="text-xs text-[#a11a3c] mt-2">Marge agence : {dv.margin_percent}% (+{Math.round(dv.base_cost - (dv.price_per_person_base * dv.passenger_count))}€)</p>
                )}
                {dv.sent_at && <p className="text-xs text-gray-400 mt-1">Envoyé le {formatDateFr(dv.sent_at.split('T')[0])}</p>}
                {dv.signed_at && <p className="text-xs text-emerald-600 mt-1">Signé le {formatDateFr(dv.signed_at.split('T')[0])} par {dv.signed_by}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── DEVIS BUILDER (pre-filled form) ──

function DevisBuilder({ init, cruises = [], authHeaders, onClose, onCreated }) {
  const { agency } = useAgency();
  const commissionType = agency?.commission_type || 'deduct';
  const commissionRate = agency?.commission_rate || 0;

  // init can be: {cruise, date} (from cruise list) or {cruise, date, prefill} (from request) or {} (blank)
  // When the agency starts a blank quote, allow them to pick the cruise + date themselves.
  const initCruise = init?.cruise && init.cruise.id ? init.cruise : null;
  const initDate = init?.date && init.date.id ? init.date : null;
  const prefill = init?.prefill || {};

  const eligibleCruises = (cruises || []).filter(c => (c.available_dates || []).length > 0);

  const [selectedCruiseId, setSelectedCruiseId] = useState(initCruise?.id || '');
  const [selectedDateId, setSelectedDateId] = useState(initDate?.id || '');

  // Resolve the active cruise (from list when no init, else from init) and date.
  const cruise = selectedCruiseId
    ? (eligibleCruises.find(c => c.id === selectedCruiseId) || initCruise || {})
    : (initCruise || {});
  const availableDates = (cruise.available_dates || []);
  const date = selectedDateId
    ? (availableDates.find(d => d.id === selectedDateId) || initDate || {})
    : (initDate || {});

  const priceBase = date.cabin_price || date.price_per_person || 0;
  const durationStr = cruise.duration || '';
  const isWeek2 = durationStr.includes('14');
  const caissePP = isWeek2 ? 480 : 240;

  // Initial margin depends on commission type:
  // - deduct → 0% (agency sells at SOGNUDIMARE price, commission is paid back to them)
  // - markup → commission_rate% (agency adds its commission to the price)
  const defaultMargin = commissionType === 'markup' ? commissionRate : 0;

  const [form, setForm] = useState({
    client_name: prefill.client_name || '',
    client_email: '',
    client_phone: '',
    passenger_count: prefill.passenger_count || 2,
    is_solo: prefill.is_solo || false,
    margin_percent: defaultMargin,
    intro_text: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handlePassengerChange = (n) => {
    setForm(f => ({ ...f, passenger_count: n, is_solo: n === 1 ? f.is_solo : false }));
  };

  // Live pricing calculation
  const pax = form.passenger_count;
  const marginMult = 1 + form.margin_percent / 100;
  const priceClient = Math.round(priceBase * marginMult);
  const baseCost = form.is_solo
    ? Math.round(priceClient * 2 * 0.9 * pax)
    : priceClient * pax;
  const caisseTotal = caissePP * pax;
  const totalTTC = baseCost + caisseTotal;
  const marginAmount = Math.round((priceBase * form.margin_percent / 100) * (form.is_solo ? 2 * 0.9 * pax : pax));

  const p35 = Math.round(totalTTC * 0.35);
  const solde = totalTTC - p35 * 2;

  const handleSave = async () => {
    if (!cruise.id || !date.id) { toast.error('Veuillez choisir une croisière et une date'); return; }
    if (!form.client_name.trim()) { toast.error('Le nom du client est requis'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cruise_id: cruise.id || '',
        date_id: date.id || '',
        cruise_name: cruise.name || '',
        start_date: date.start_date || '',
        end_date: date.end_date || '',
        duration: durationStr,
        departure_port: cruise.departure_port || 'Ajaccio',
        price_per_person: priceBase,
      };
      const r = await axios.post(`${API}/agency/devis`, payload, { headers: authHeaders });
      toast.success(`${r.data.devis.devis_number} créé !`);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="devis-builder">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Nouveau devis</h2>
          <p className="text-sm text-gray-400 mt-0.5">{cruise.name ? `${cruise.name} — ${formatDateFr(date.start_date)} → ${formatDateFr(date.end_date)}` : 'Devis personnalisé'}</p>
        </div>
        <Button variant="outline" onClick={onClose} data-testid="devis-builder-cancel">Annuler</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left column - Client info + options */}
        <div className="space-y-5">
          {!initCruise && (
            <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="devis-cruise-selector">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Calendar className="h-4 w-4 text-[#a11a3c]" /> Choix de la croisière</h3>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Croisière *</label>
                <select
                  className="w-full border rounded-md h-10 px-3 text-sm bg-white"
                  value={selectedCruiseId}
                  onChange={e => { setSelectedCruiseId(e.target.value); setSelectedDateId(''); }}
                  data-testid="devis-cruise-select"
                >
                  <option value="">— Sélectionnez une croisière —</option>
                  {eligibleCruises.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.duration})</option>
                  ))}
                </select>
                {eligibleCruises.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Aucune croisière n'a de date disponible actuellement.</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Date de départ *</label>
                <select
                  className="w-full border rounded-md h-10 px-3 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
                  value={selectedDateId}
                  onChange={e => setSelectedDateId(e.target.value)}
                  disabled={!selectedCruiseId || availableDates.length === 0}
                  data-testid="devis-date-select"
                >
                  <option value="">— Sélectionnez une date —</option>
                  {availableDates.map(d => (
                    <option key={d.id} value={d.id}>
                      {formatDateFr(d.start_date)} → {formatDateFr(d.end_date)} ({d.cabin_price}€/pers · {d.available_cabins} cab.)
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border p-5 space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Users className="h-4 w-4 text-[#a11a3c]" /> Informations client</h3>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Nom complet *</label>
              <Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                placeholder="Jean Dupont" data-testid="devis-client-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Email</label>
              <Input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
                placeholder="jean@exemple.com" data-testid="devis-client-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Téléphone</label>
              <Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))}
                placeholder="06 12 34 56 78" data-testid="devis-client-phone" />
            </div>
          </div>

          <div className="bg-white rounded-xl border p-5 space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Ship className="h-4 w-4 text-[#a11a3c]" /> Options de la croisière</h3>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Passagers</label>
              <select className="w-full border rounded-md h-10 px-3 text-sm" value={pax}
                onChange={e => handlePassengerChange(parseInt(e.target.value))} data-testid="devis-pax">
                {Array.from({ length: 8 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n} passager{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            {pax === 1 && (
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border cursor-pointer" data-testid="devis-solo-toggle">
                <input type="checkbox" checked={form.is_solo}
                  onChange={e => setForm(f => ({ ...f, is_solo: e.target.checked }))}
                  className="rounded border-gray-300 text-[#a11a3c] focus:ring-[#a11a3c]" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Cabine solo</span>
                  <p className="text-xs text-gray-400">Cabine double occupée seul (-10%)</p>
                </div>
              </label>
            )}
            <div>
              {commissionType === 'deduct' ? (
                <div className="rounded-lg border-2 border-[#2A9D8F]/30 bg-[#2A9D8F]/5 p-3" data-testid="commission-mode-deduct-info">
                  <div className="flex items-start gap-2.5">
                    <span className="text-lg leading-none mt-0.5">📉</span>
                    <div className="text-xs text-[#0A2540] leading-snug">
                      <strong className="text-[#2A9D8F]">Mode commission déduite — Vente au prix SOGNUDIMARE</strong>
                      <p className="text-[#4A5568] mt-1">
                        Vous vendez la croisière au prix public ({priceBase}€/pers). La commission de{' '}
                        <strong className="text-[#2A9D8F]">{commissionRate}%</strong> vous est reversée par SOGNUDIMARE sur chaque réservation confirmée.
                        <br />
                        💰 Votre revenu :{' '}
                        <strong className="text-[#2A9D8F]">
                          {Math.round(priceBase * pax * commissionRate / 100)}€
                        </strong>{' '}
                        pour {pax} passager{pax > 1 ? 's' : ''}.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <label className="text-sm font-medium text-gray-600 block mb-1">Marge agence (%)</label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="0" max="50" step="1" value={form.margin_percent}
                      onChange={e => setForm(f => ({ ...f, margin_percent: parseInt(e.target.value) }))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#a11a3c]"
                      data-testid="devis-margin-slider" />
                    <div className="flex items-center bg-gray-50 border rounded-md px-2 min-w-[70px]">
                      <input type="number" min="0" max="50" value={form.margin_percent}
                        onChange={e => setForm(f => ({ ...f, margin_percent: Math.min(50, Math.max(0, parseInt(e.target.value) || 0)) }))}
                        className="w-10 text-center text-sm font-bold text-[#a11a3c] bg-transparent border-0 focus:outline-none"
                        data-testid="devis-margin-input" />
                      <span className="text-sm font-bold text-gray-500">%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-[11px] text-[#a11a3c]">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C4724F]" />
                    <span>
                      Mode commission ajoutée — taux conseillé : <strong>{commissionRate}%</strong>
                    </span>
                  </div>
                  {form.margin_percent > 0 && (
                    <p className="text-xs text-[#a11a3c] mt-1">+{marginAmount}€ — client voit {priceClient}€/pers.</p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border p-5 space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Edit className="h-4 w-4 text-[#a11a3c]" /> Personnalisation</h3>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Message d'introduction</label>
              <Textarea value={form.intro_text} onChange={e => setForm(f => ({ ...f, intro_text: e.target.value }))}
                placeholder="Suite à notre entretien, nous avons le plaisir de vous proposer..."
                rows={3} data-testid="devis-intro" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Notes internes (non visibles client)</label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes pour votre suivi interne..."
                rows={2} className="text-sm" data-testid="devis-notes" />
            </div>
          </div>
        </div>

        {/* Right column - Live preview */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl border overflow-hidden sticky top-6" data-testid="devis-preview">
            <div className="bg-[#0A2540] text-white p-5">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Aperçu du devis</p>
              <h3 className="font-bold text-lg">{cruise.name || 'Croisière'}</h3>
              <p className="text-white/50 text-sm">{durationStr} — LAGOON 46 / 43 / 38</p>
              <p className="text-white/40 text-xs mt-1">{formatDateFr(date.start_date)} → {formatDateFr(date.end_date)}</p>
            </div>

            <div className="p-5 space-y-4">
              {form.client_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 font-medium">{form.client_name}</span>
                </div>
              )}

              {/* Pricing summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tarification client</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    {form.is_solo ? `${pax} cab. solo` : `${pax} passager(s) x ${priceClient}€`}
                    {form.margin_percent > 0 ? ` (+${form.margin_percent}%)` : ''}
                  </span>
                  <span className="font-medium">{baseCost}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Caisse de bord ({caissePP}€ x {pax})</span>
                  <span className="font-medium">{caisseTotal}€</span>
                </div>
                {form.margin_percent > 0 && (
                  <div className="flex justify-between text-xs text-[#a11a3c]">
                    <span>Dont marge agence</span>
                    <span className="font-semibold">+{marginAmount}€</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-bold text-[#0A2540]">TOTAL TTC</span>
                  <span className="font-bold text-xl text-[#C4724F]">{totalTTC}€</span>
                </div>
              </div>

              {/* Payment schedule */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Échéancier de paiement</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">35% à la validation</span>
                  <span className="font-medium">{p35}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">35% à J+30</span>
                  <span className="font-medium">{p35}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Solde</span>
                  <span className="font-medium">{solde}€</span>
                </div>
              </div>

              {form.intro_text && (
                <div className="border-l-3 border-[#a11a3c] pl-3">
                  <p className="text-xs text-gray-400 mb-1">Message personnalisé</p>
                  <p className="text-sm text-gray-600 italic whitespace-pre-wrap">{form.intro_text}</p>
                </div>
              )}
            </div>

            <div className="border-t p-5">
              <Button className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90 h-11" onClick={handleSave} disabled={saving} data-testid="devis-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="h-4 w-4 mr-2" /> Créer le devis</>}
              </Button>
              <p className="text-xs text-gray-400 text-center mt-2">Vous pourrez ensuite l'envoyer par email au client</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main Export ---
export default function AgencyPortal() {
  return (
    <AgencyProvider>
      <AgencyPortalInner />
    </AgencyProvider>
  );
}


// ── Purchase Orders / Invoices Tab ──
const ORDER_STATUS = {
  en_attente: { label: 'En attente', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  paye: { label: 'Facture acquittée', cls: 'bg-emerald-100 text-emerald-700', icon: Check },
  annule: { label: 'Annulé', cls: 'bg-red-100 text-red-700', icon: X },
};

function PurchaseOrdersTab({ orders, requests, cruises, authHeaders, onRefresh }) {
  const [creating, setCreating] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);

  // Confirmed requests that don't have a purchase order yet
  const eligibleRequests = requests?.filter(r =>
    (r.status === 'approved' || r.status === 'confirmed') && !r.purchase_order_id
  ) || [];

  const handleCreate = async (requestId, clientData) => {
    setCreating(requestId);
    try {
      await axios.post(`${API}/agency/purchase-orders`, { request_id: requestId, ...clientData }, { headers: authHeaders });
      toast.success('Bon de commande créé');
      setShowCreateForm(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de création');
    } finally {
      setCreating(null);
    }
  };

  const handleManualCreate = async (manualData) => {
    setCreating('manual');
    try {
      await axios.post(`${API}/agency/purchase-orders`, { manual: true, ...manualData }, { headers: authHeaders });
      toast.success('Bon de commande créé');
      setShowManualForm(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de création');
    } finally {
      setCreating(null);
    }
  };

  const handlePdf = async (order) => {
    setDownloading(order.id);
    try {
      const resp = await axios.get(`${API}/agency/purchase-orders/${order.id}/pdf`, {
        headers: authHeaders, responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Erreur téléchargement PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteOrder = async (order) => {
    const label = order.order_number || order.id;
    if (!window.confirm(`Supprimer le bon de commande ${label} ?`)) return;
    setDeleting(order.id);
    try {
      await axios.delete(`${API}/agency/purchase-orders/${order.id}`, { headers: authHeaders });
      toast.success('Bon de commande supprimé');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleCancelOrder = async (order) => {
    const label = order.order_number || order.id;
    if (!window.confirm(`Annuler le bon de commande ${label} ? Cette action est irréversible.`)) return;
    setCancelling(order.id);
    try {
      await axios.post(`${API}/agency/purchase-orders/${order.id}/cancel`, {}, { headers: authHeaders });
      toast.success('Bon de commande annulé');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur annulation');
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="purchase-orders-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl font-bold text-gray-900">Bons de commande / Factures</h2>
        <Button size="sm" className="bg-[#0A2540] hover:bg-[#0A2540]/90"
          onClick={() => setShowManualForm(true)} data-testid="create-manual-po-btn">
          <Plus className="h-4 w-4 mr-1" /> Créer un bon de commande
        </Button>
      </div>

      {/* Eligible requests for new purchase orders */}
      {eligibleRequests.length > 0 && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <p className="text-sm font-semibold text-blue-800 mb-3">Demandes éligibles pour un bon de commande :</p>
          <div className="space-y-2">
            {eligibleRequests.map(req => (
              <div key={req.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white rounded-lg p-3 border">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{req.cruise_name}</p>
                  <p className="text-xs text-gray-500">{formatDateFr(req.start_date)} → {formatDateFr(req.end_date)} &middot; {req.client_name} &middot; {req.passenger_count} pax</p>
                </div>
                <Button size="sm" className="bg-[#0A2540] hover:bg-[#0A2540]/90 h-8 text-xs shrink-0"
                  onClick={() => setShowCreateForm(req)} data-testid={`create-po-${req.id}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Bon de commande
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing orders */}
      {orders.length === 0 && eligibleRequests.length === 0 ? (
        <p className="text-gray-400 text-center py-12">Aucun bon de commande</p>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const st = ORDER_STATUS[order.status] || ORDER_STATUS.en_attente;
            const isPaid = order.status === 'paye';
            const docNumber = isPaid ? order.invoice_number : order.order_number;
            const schedule = order.payment_schedule || [];
            const paidCount = schedule.filter(s => s.status === 'paid').length;
            return (
              <div key={order.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`po-card-${order.id}`}>
                <div className={`h-1 ${isPaid ? 'bg-emerald-500' : order.status === 'annule' ? 'bg-red-400' : 'bg-amber-400'}`} />
                <div className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-[#0A2540]">{docNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mt-1">{order.cruise_name}</p>
                      <p className="text-xs text-gray-500">{formatDateFr(order.start_date)} → {formatDateFr(order.end_date)} &middot; {order.passenger_count} pax</p>
                      <p className="text-xs text-gray-400 mt-0.5">Client : {order.client?.name || '-'}</p>
                      {isPaid && order.invoice_number && (
                        <p className="text-xs text-emerald-600 font-medium mt-1">Facture : {order.invoice_number}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-start">
                      <span className="text-lg font-bold text-[#C4724F]">{order.total_ttc}€</span>
                      <Button size="sm" variant="outline" onClick={() => handlePdf(order)}
                        disabled={downloading === order.id} className="h-8 text-xs" data-testid={`po-pdf-${order.id}`}>
                        {downloading === order.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Eye className="h-3.5 w-3.5 mr-1" /> PDF</>}
                      </Button>
                      {order.status !== 'paye' && order.status !== 'annule' && (
                        <Button size="sm" variant="ghost" onClick={() => handleCancelOrder(order)}
                          disabled={cancelling === order.id}
                          className="h-8 text-xs text-amber-500 hover:text-amber-700 hover:bg-amber-50" data-testid={`po-cancel-${order.id}`}>
                          {cancelling === order.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><X className="h-3.5 w-3.5 mr-1" /> Annuler</>}
                        </Button>
                      )}
                      {order.status !== 'paye' && (
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteOrder(order)}
                          disabled={deleting === order.id}
                          className="h-8 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`po-delete-${order.id}`}>
                          {deleting === order.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Passengers */}
                  {order.passengers?.length > 0 && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Passagers</p>
                      <div className="space-y-0.5">
                        {order.passengers.map((p, i) => (
                          <p key={i} className="text-xs text-gray-600">
                            {i+1}. <span className="font-medium">{p.last_name?.toUpperCase()} {p.first_name}</span>
                            <span className="text-gray-400 ml-2">né(e) le {formatDateFr(p.date_of_birth)} à {p.place_of_birth}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment schedule */}
                  {schedule.length > 0 && !isPaid && order.status !== 'annule' && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Échéancier de paiement ({paidCount}/{schedule.length})
                      </p>
                      <div className="space-y-1.5">
                        {schedule.map((s, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                            <div className="flex items-center gap-2 min-w-0">
                              {s.status === 'paid' ? (
                                <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                              ) : (
                                <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                              )}
                              <span className="text-gray-600">{s.label}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="font-medium">{s.amount}€</span>
                              {s.status === 'paid' ? (
                                <span className="text-emerald-600 font-medium">Payé</span>
                              ) : (
                                <span className="text-amber-600">avant {formatDateFr(s.due_date)}</span>
                              )}
                              {s.square_link && s.status !== 'paid' && (
                                <a href={s.square_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Payer</a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Purchase Order Modal — from request */}
      {showCreateForm && (
        <PurchaseOrderCreateModal
          request={showCreateForm}
          creating={creating}
          onCreate={handleCreate}
          onClose={() => setShowCreateForm(null)}
        />
      )}

      {/* Create Manual Purchase Order Modal */}
      {showManualForm && (
        <ManualPurchaseOrderModal
          cruises={cruises}
          creating={creating === 'manual'}
          onCreate={handleManualCreate}
          onClose={() => setShowManualForm(false)}
        />
      )}
    </div>
  );
}

function PurchaseOrderCreateModal({ request: req, creating, onCreate, onClose }) {
  const [clientName, setClientName] = useState(req.client_name || '');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');

  const pax = req.passenger_count || 0;
  const [passengers, setPassengers] = useState(
    Array.from({ length: pax }, () => ({ last_name: '', first_name: '', date_of_birth: '', place_of_birth: '' }))
  );

  const updatePassenger = (idx, field, value) => {
    setPassengers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clientName.trim()) { toast.error('Le nom du client est requis'); return; }
    const incomplete = passengers.some(p => !p.last_name.trim() || !p.first_name.trim() || !p.date_of_birth || !p.place_of_birth.trim());
    if (incomplete) { toast.error('Tous les champs passagers sont obligatoires (nom, prénom, date et lieu de naissance)'); return; }
    onCreate(req.id, {
      client_name: clientName.trim(),
      client_email: clientEmail.trim(),
      client_phone: clientPhone.trim(),
      client_address: clientAddress.trim(),
      passengers,
    });
  };

  const priceBase = req.price_per_person_base || 0;
  const caissePp = req.caisse_per_person || 0;
  const isSolo = req.is_solo;
  const totalCruise = isSolo ? Math.round(priceBase * 2 * 0.9 * pax) : priceBase * pax;
  const totalCaisse = caissePp * pax;
  const totalTtc = totalCruise + totalCaisse;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="po-create-modal">
        <div className="bg-[#0A2540] text-white p-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-lg">Bon de commande</h2>
            <button onClick={onClose} className="text-white/50 hover:text-white text-xl">&times;</button>
          </div>
          <p className="text-white/50 text-sm mt-1">{req.cruise_name} &middot; {formatDateFr(req.start_date)}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Recap */}
          <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Croisière</span><span className="font-medium">{req.cruise_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Dates</span><span>{formatDateFr(req.start_date)} → {formatDateFr(req.end_date)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Passagers</span><span>{pax}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Croisière</span><span>{totalCruise}€</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Caisse de bord</span><span>{totalCaisse}€</span></div>
            <div className="flex justify-between border-t pt-1 mt-1"><span className="font-bold text-[#0A2540]">Total TTC</span><span className="font-bold text-[#C4724F]">{totalTtc}€</span></div>
            <p className="text-xs text-gray-400 italic mt-1">TVA non applicable, article 293 B du CGI — Hors commission agence</p>
          </div>

          {/* Client Info */}
          <div className="space-y-3">
            <p className="font-semibold text-sm text-gray-700">Informations client</p>
            <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nom complet du client *" required data-testid="po-client-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="Email du client" data-testid="po-client-email" />
              <Input value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="Téléphone" data-testid="po-client-phone" />
            </div>
            <Input value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder="Adresse postale" data-testid="po-client-address" />
          </div>

          {/* Passengers List */}
          <div className="space-y-3">
            <p className="font-semibold text-sm text-gray-700">Liste des passagers <span className="text-xs text-red-500 font-normal">(obligatoire pour la liste d'embarquement)</span></p>
            {passengers.map((p, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-3 space-y-2">
                <p className="text-xs font-bold text-gray-500 uppercase">Passager {idx + 1}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={p.last_name} onChange={e => updatePassenger(idx, 'last_name', e.target.value)}
                    placeholder="Nom *" required data-testid={`pax-last-${idx}`} />
                  <Input value={p.first_name} onChange={e => updatePassenger(idx, 'first_name', e.target.value)}
                    placeholder="Prénom *" required data-testid={`pax-first-${idx}`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Date de naissance *</label>
                    <FrenchDatePicker value={p.date_of_birth} onChange={v => updatePassenger(idx, 'date_of_birth', v)}
                      required placeholder="Date de naissance" data-testid={`pax-dob-${idx}`} />
                  </div>
                  <Input value={p.place_of_birth} onChange={e => updatePassenger(idx, 'place_of_birth', e.target.value)}
                    placeholder="Lieu de naissance *" required className="mt-auto" data-testid={`pax-pob-${idx}`} />
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="flex-1 bg-[#0A2540] hover:bg-[#0A2540]/90" disabled={!!creating} data-testid="po-submit-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Créer le bon de commande
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ManualPurchaseOrderModal({ cruises, creating, onCreate, onClose }) {
  const [selectedCruise, setSelectedCruise] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [paxCount, setPaxCount] = useState(2);
  const [cabins, setCabins] = useState(1);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [passengers, setPassengers] = useState([{ last_name: '', first_name: '', date_of_birth: '', place_of_birth: '' }, { last_name: '', first_name: '', date_of_birth: '', place_of_birth: '' }]);

  const updatePaxCount = (n) => {
    const num = Math.max(1, Math.min(12, n));
    setPaxCount(num);
    setPassengers(prev => {
      if (num > prev.length) return [...prev, ...Array.from({ length: num - prev.length }, () => ({ last_name: '', first_name: '', date_of_birth: '', place_of_birth: '' }))];
      return prev.slice(0, num);
    });
  };

  const updatePassenger = (idx, field, value) => {
    setPassengers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const cruise = selectedCruise;
  const date = selectedDate;
  const priceBase = date?.price_per_cabin || date?.cabin_price || 0;
  const durationStr = cruise?.duration || '';
  const isTwoWeeks = /2\s*semaine|14\s*jour/i.test(durationStr);
  const caissePp = isTwoWeeks ? 480 : 240;
  const totalCruise = priceBase * paxCount;
  const totalCaisse = caissePp * paxCount;
  const totalTtc = totalCruise + totalCaisse;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCruise || !selectedDate) { toast.error('Sélectionnez une croisière et une date'); return; }
    if (!clientName.trim()) { toast.error('Le nom du client est requis'); return; }
    const incomplete = passengers.some(p => !p.last_name.trim() || !p.first_name.trim() || !p.date_of_birth || !p.place_of_birth.trim());
    if (incomplete) { toast.error('Tous les champs passagers sont obligatoires'); return; }

    onCreate({
      cruise_name: cruise.name,
      start_date: date.start_date,
      end_date: date.end_date,
      duration: durationStr,
      passenger_count: paxCount,
      cabins,
      price_per_person: priceBase,
      caisse_per_person: caissePp,
      is_solo: false,
      client_name: clientName.trim(),
      client_email: clientEmail.trim(),
      client_phone: clientPhone.trim(),
      client_address: clientAddress.trim(),
      passengers,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="manual-po-modal">
        <div className="bg-[#0A2540] text-white p-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-lg">Nouveau bon de commande</h2>
            <button onClick={onClose} className="text-white/50 hover:text-white text-xl">&times;</button>
          </div>
          <p className="text-white/50 text-sm mt-1">Création manuelle — Devis interne</p>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Cruise selection */}
          <div className="space-y-3">
            <p className="font-semibold text-sm text-gray-700">Croisière</p>
            <select className="w-full border rounded-lg px-3 py-2 text-sm" value={selectedCruise?.id || ''}
              onChange={e => { const c = cruises.find(cr => cr.id === e.target.value); setSelectedCruise(c || null); setSelectedDate(null); }}
              data-testid="manual-po-cruise">
              <option value="">Sélectionner une croisière</option>
              {cruises.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            {selectedCruise?.dates?.length > 0 && (
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={selectedDate?.id || ''}
                onChange={e => { const d = selectedCruise.dates.find(dt => dt.id === e.target.value); setSelectedDate(d || null); }}
                data-testid="manual-po-date">
                <option value="">Sélectionner une date</option>
                {selectedCruise.dates.filter(d => d.is_public !== false).map(d => (
                  <option key={d.id} value={d.id}>
                    {formatDateFr(d.start_date)} → {formatDateFr(d.end_date)} — {d.price_per_cabin || d.cabin_price || 0}€/pers
                  </option>
                ))}
              </select>
            )}
          </div>

          {selectedDate && (
            <>
              {/* Passengers count + cabins */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Nombre de passagers</label>
                  <Input type="number" min={1} max={12} value={paxCount} onChange={e => updatePaxCount(parseInt(e.target.value) || 1)} data-testid="manual-po-pax" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Cabines</label>
                  <Input type="number" min={1} max={6} value={cabins} onChange={e => setCabins(parseInt(e.target.value) || 1)} data-testid="manual-po-cabins" />
                </div>
              </div>

              {/* Price recap */}
              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Croisière ({paxCount} pax × {priceBase}€)</span><span>{totalCruise}€</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Caisse de bord ({paxCount} × {caissePp}€)</span><span>{totalCaisse}€</span></div>
                <div className="flex justify-between border-t pt-1 mt-1"><span className="font-bold text-[#0A2540]">Total TTC</span><span className="font-bold text-[#C4724F]">{totalTtc}€</span></div>
                <p className="text-xs text-gray-400 italic">TVA non applicable, article 293 B du CGI — Hors commission agence</p>
              </div>

              {/* Client */}
              <div className="space-y-3">
                <p className="font-semibold text-sm text-gray-700">Client</p>
                <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nom complet *" required data-testid="manual-po-client-name" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="Email" data-testid="manual-po-client-email" />
                  <Input value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="Téléphone" data-testid="manual-po-client-phone" />
                </div>
                <Input value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder="Adresse postale" data-testid="manual-po-client-address" />
              </div>

              {/* Passengers */}
              <div className="space-y-3">
                <p className="font-semibold text-sm text-gray-700">Passagers <span className="text-xs text-red-500 font-normal">(obligatoire)</span></p>
                {passengers.map((p, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-bold text-gray-500 uppercase">Passager {idx + 1}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={p.last_name} onChange={e => updatePassenger(idx, 'last_name', e.target.value)} placeholder="Nom *" required data-testid={`mpax-last-${idx}`} />
                      <Input value={p.first_name} onChange={e => updatePassenger(idx, 'first_name', e.target.value)} placeholder="Prénom *" required data-testid={`mpax-first-${idx}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Date de naissance *</label>
                        <FrenchDatePicker value={p.date_of_birth} onChange={v => updatePassenger(idx, 'date_of_birth', v)}
                          required placeholder="Date de naissance" data-testid={`mpax-dob-${idx}`} />
                      </div>
                      <Input value={p.place_of_birth} onChange={e => updatePassenger(idx, 'place_of_birth', e.target.value)} placeholder="Lieu de naissance *" required className="mt-auto" data-testid={`mpax-pob-${idx}`} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
                <Button type="submit" className="flex-1 bg-[#0A2540] hover:bg-[#0A2540]/90" disabled={creating} data-testid="manual-po-submit">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Créer le bon de commande
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}


// ── Agency Contract Tab ──
function AgencyContractTab({ contract }) {
  const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api','') || '';
  const signUrl = contract.sign_token ? `${SITE_URL}/contrat/${contract.sign_token}` : null;

  const statusLabels = {
    sent: { label: 'En attente de votre signature', cls: 'bg-amber-100 text-amber-700' },
    signed_armateur: { label: 'En attente de votre signature', cls: 'bg-blue-100 text-blue-700' },
    signed_agency: { label: 'Signé par vous', cls: 'bg-purple-100 text-purple-700' },
    active: { label: 'Actif', cls: 'bg-emerald-100 text-emerald-700' },
  };
  const st = statusLabels[contract.status] || { label: contract.status, cls: 'bg-gray-100 text-gray-700' };

  return (
    <div className="space-y-5" data-testid="agency-contract-tab">
      <h2 className="text-xl font-bold text-gray-900">Contrat de partenariat</h2>
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-[#0A2540] via-[#0A2540] to-[#C4724F]" />
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-mono text-gray-400">{contract.reference}</span>
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-[#C4724F] uppercase mb-1">Armateur</p>
              <p className="font-bold text-[#0A2540]">{contract.armateur?.name}</p>
              {contract.armateur?.address && <p className="text-sm text-gray-500">{contract.armateur.address}</p>}
              {contract.armateur_signed_at && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Signé le {new Date(contract.armateur_signed_at).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-[#C4724F] uppercase mb-1">Agence</p>
              <p className="font-bold text-[#0A2540]">{contract.agency_info?.name}</p>
              {contract.agency_info?.address && <p className="text-sm text-gray-500">{contract.agency_info.address}</p>}
              {contract.agency_signed_at && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Signé le {new Date(contract.agency_signed_at).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            <Calendar className="h-4 w-4 inline mr-1" />
            Durée : <strong>{contract.duration || '1 an'}</strong> (tacite reconduction)
          </p>

          <div className="flex gap-2 flex-wrap">
            {signUrl && !contract.agency_signed_at && (
              <Button className="bg-[#C4724F] hover:bg-[#a8603f] gap-2"
                onClick={() => window.open(signUrl, '_blank')}
                data-testid="agency-sign-contract-btn">
                <ExternalLink className="h-4 w-4" /> Consulter et signer le contrat
              </Button>
            )}
            {signUrl && (
              <Button variant="outline" className="gap-2"
                onClick={() => window.open(`${API}/partnership-contract/public/${contract.sign_token}/pdf`, '_blank')}
                data-testid="agency-download-contract-btn">
                <FileDown className="h-4 w-4" /> Télécharger le PDF
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function AgencyPortalInner() {
  const { agency, loading } = useAgency();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C4724F]" /></div>;
  return agency ? <AgencyDashboardContent /> : <AgencyLoginForm />;
}
